// Other External Files
import { firstValueFrom, Observable, Subject } from 'rxjs';

// Payment Integration Files
import {
    IFramePaymentResponse,
    IFrameSavePaymentMethodResponse,
    PaymentMethodTypeEnum,
    RedirectPaymentProcessorResponse,
    PaymentMethodData,
    PaymentMethodTypeEnumConvertor
} from 'apps/public-portal/src/app/payment-integrations/base/models';

// Teller Online Files
import {
    SharedPaymentIntegrationApiClient,
    IFrameSavePaymentMethodRequestDto,
    IFramePaymentRequestDto,
    ECheckAccountTypeEnumDto,
    ECheckAccountOwnerTypeEnumDto,
} from 'apps/public-portal/src/app/core/api/PaymentIntegrationApiClients';
import { Country } from 'apps/public-portal/src/app/shared/constants';
import { IFramePaymentPrepareResponse } from 'apps/public-portal/src/app/payment-integrations/base/models/IFramePaymentPrepareResponse';

export abstract class PaymentProcessorService {
    protected _errorEvent: Subject<string> = new Subject();

    public errorEvent$: Observable<string> = this._errorEvent.asObservable();

    public supportsTokenization: boolean;

    /** Whether this processor redirects to an external site for payment. Override in subclass. */
    public static redirect: boolean = false;

    /** Instance accessor for the static redirect property */
    public get redirect(): boolean { return (this.constructor as typeof PaymentProcessorService).redirect; }

    public systemPaymentIdentifierKey: string;

    protected constructor(protected sharedPaymentApi: SharedPaymentIntegrationApiClient) { }

    /** Handles appropriate redirects in order to checkout:
     * Checkout page for iframes
     * External url for redirects
     */
    public abstract checkout(cartGuid?: string): any;

    /**
     * Retrieve a unique hash of the current cart to compare if it's changed or not
     * @param cartGuid The cart guid to get a hash of
     * @param paymentIdentifier (optional), the payment identifier to include when hash checking
     * @returns A string hash representing the cart to be compared for uniqueness
     */
    public async getCartHash(paymentMethodType: PaymentMethodTypeEnum, cartGuid:string, paymentIdentifier: string = null) {
        return firstValueFrom(this.sharedPaymentApi.getCartHash(
            PaymentMethodTypeEnumConvertor.toDto(paymentMethodType),
            cartGuid,
            paymentIdentifier));
    }

    /** Handles a postback response from a redirect integration */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public handlePostbackResponse(params): Promise<RedirectPaymentProcessorResponse> {
        // Do nothing
        return new Promise<any>(null);
    }

    public get hasPrepareStep() { return false; }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public preparePayment(params): Promise<IFramePaymentPrepareResponse> {
        return new Promise<any>(null);
    }

    /** Handles paying for a cart from the checkout page for an iframe integration */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public payCart(params): Promise<IFramePaymentResponse> {
        // Do nothing
        return new Promise<IFramePaymentResponse>(null);
    }

    /**
     * Hook called between Prepare and Pay on the saved-method flow. Lets a
     * processor inspect the prepare response and run any extra steps it needs
     * before authorize — e.g. lazy re-tokenization with a CVV prompt for
     * Generic. Returns true when the processor is ready to authorize, false
     * when the user cancelled an interactive step (e.g. closed the CVV
     * dialog) and pay should be aborted gracefully. Default no-op resolves
     * true so non-Generic processors are unaffected.
     */
    /* eslint-disable @typescript-eslint/no-unused-vars */
    public async ensureSavedMethodReadyForPay(
        cartId: number,
        cartGuid: string,
        prepareResponse: IFramePaymentPrepareResponse,
        paymentMethodType: PaymentMethodTypeEnum
    ): Promise<boolean> {
        return true;
    }
    /* eslint-enable @typescript-eslint/no-unused-vars */

    /** Handles updating a payment method for an iframe integration */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public savePaymentMethod(params): Promise<IFrameSavePaymentMethodResponse> {
        // Do nothing
        return new Promise<IFrameSavePaymentMethodResponse>(null);
    }

    /** By default, do nothing, but allow a processor to perform specific logic when the page is being deactivated */
    public async deactivateCheckout() {
        // Default no-op; processors override to clean up Prepare-time state.
    }

    // #region helpers

    /**
     * Assigns processor specific properties to the service. Can be overidden if necessary
     * (e.g. additional property names are different than service property name).
     * @param additionalProps Object with properties to be mapped to the service
     * @param processorType Specify which type of payment type the service is being configured for.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public assignAdditionalProps(additionalProps: { [key: string]: any }, processorType?: PaymentMethodTypeEnum) {
        Object.keys(additionalProps).forEach(key => {
            // Ensure boolean values get mapped properly because the backend converts the boolean value to a string
            if (additionalProps[key] === 'True') additionalProps[key] = true;
            else if (additionalProps[key] === 'False') additionalProps[key] = false;
        });

        // Credit and e-check can resolve to the same service instance, and the
        // provider assigns each rail's props in turn — a rail whose config omits
        // a value (null/undefined) must not clear the other rail's value.
        const definedProps = Object.fromEntries(
            Object.entries(additionalProps).filter(([, value]) => value != null)
        );

        Object.assign(this, definedProps);
    }

    protected mapRequest(request: IFrameSavePaymentMethodRequestDto | IFramePaymentRequestDto,
                         paymentMethodData: PaymentMethodData,
                         token?: string,
                         testTrigger?: string) {
        request.paymentMethodType ??= PaymentMethodTypeEnumConvertor.toDto(paymentMethodData.paymentMethodType);

        if (paymentMethodData.billingInfo.fullName) request.fullName = paymentMethodData.billingInfo.fullName;
        if (paymentMethodData.billingInfo.addressZip) request.addressZip = paymentMethodData.billingInfo.addressZip;
        if (paymentMethodData.billingInfo.addressLine1) request.addressLine1 = paymentMethodData.billingInfo.addressLine1;
        if (paymentMethodData.billingInfo.addressLine2) request.addressLine2 = paymentMethodData.billingInfo.addressLine2;
        if (paymentMethodData.billingInfo.addressCity) request.addressCity = paymentMethodData.billingInfo.addressCity;
        if (paymentMethodData.billingInfo.phone) request.phone = paymentMethodData.billingInfo.phone;
        if (token) request.token = token;
        if (testTrigger) request.paymentProcessorTestTrigger = testTrigger;

        if (paymentMethodData.billingInfo.addressCountry?.name) request.addressCountry = paymentMethodData.billingInfo.addressCountry.name;
        if (paymentMethodData.billingInfo.addressCountry?.code) request.addressCountryCode = paymentMethodData.billingInfo.addressCountry.code;

        if (Country.HasRegionList(request.addressCountryCode)) {
            request.addressStateCode = paymentMethodData.billingInfo.addressState?.code ?? undefined;
            request.addressState = paymentMethodData.billingInfo.addressState?.name ?? undefined;
        } else {
            request.addressStateCode = paymentMethodData.billingInfo.addressRegion ?? undefined;
            request.addressState = paymentMethodData.billingInfo.addressRegion ?? undefined;
        }

        switch (paymentMethodData.type) {
            case PaymentMethodTypeEnum.CreditCard:
                request.cardType = paymentMethodData.cardType;
                if (paymentMethodData.digitalWalletType) {
                    // Wallet-flavored credit: last4 comes from the tokenize
                    // response, not a typed card number. No expiry either —
                    // wallets don't surface it.
                    if (paymentMethodData.last4Digits) request.last4 = paymentMethodData.last4Digits;
                } else {
                    request.cardExpiry = paymentMethodData.cardExpiry;
                    if (paymentMethodData.cardNumber) request.last4 = paymentMethodData.cardNumber.slice(-4);
                }
                break;
            case PaymentMethodTypeEnum.ECheck:
                request.last4 = paymentMethodData.echeckAccountNumber.slice(-4);
                request.eCheckAccountNumber = paymentMethodData.echeckAccountNumber;
                request.eCheckAccountType = ECheckAccountTypeEnumDto[paymentMethodData.echeckAccountType];
                request.eCheckRoutingNumber = paymentMethodData.echeckRoutingNumber;
                request.eCheckAccountOwnerType = ECheckAccountOwnerTypeEnumDto[paymentMethodData.echeckAccountOwnerType];
                break;
            default:
                break;
        }

        if (paymentMethodData.digitalWalletType) {
            (request as any).digitalWalletType = paymentMethodData.digitalWalletType;
        }
    }

    // #endregion
}

/** Type representing a PaymentProcessorService class (not instance) with its static properties */
export interface PaymentProcessorClass {
    redirect: boolean;
    new (...args: any[]): PaymentProcessorService;
}
